import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import BrokenLinkIcon from '@src/svgs/BrokenLinkIcon';
import CirclePlusIcon from '@src/svgs/CirclePlusIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import ReassignIcon from '@src/svgs/ReassignIcon';
import SpeakersIcon from '@src/svgs/SpeakersIcon';
import UndoIcon from '@src/svgs/UndoIcon';
import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

interface SpeakerContextMenuProps {
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLElement> | null; // Reference to the button that triggered the menu
  onClose: () => void;
  onEdit: () => void;
  onManageAiVoices?: () => void; // Callback for opening the Manage AI Voices modal
  onAddToStory?: () => void; // Callback for adding speaker to story
  onRemoveFromStory?: () => void; // Callback for removing speaker from story
  onRemoveFromOrganization?: () => void; // Callback for removing speaker from organization
  onUnassign?: () => void; // Callback for unassigning speaker (removing from voice mappings)
  onUndoSectionReassign?: () => void; // Callback for undoing section-level override assignment
  onUnassignAll?: () => void; // Callback for removing both voice mapping and section overrides
  onReassignAll?: () => void; // Callback for converting section overrides to full voice assignment
  onAssignToExisting?: () => void; // Callback for assigning anonymous speaker to existing Dato speaker
  isAnonymous?: boolean; // Flag to indicate if this is an anonymous speaker
  showAddToStory?: boolean; // Flag to show "Add to Story" option
  showRemoveFromStory?: boolean; // Flag to show "Remove from Story" option
  showUnassign?: boolean; // Flag to show "Unassign Speaker" option
  showUndoSectionReassign?: boolean; // Flag to show "Undo Selection Reassign" option
  showUnassignAll?: boolean; // Flag to show "Unassign All" option (both voice + override)
  showReassignAll?: boolean; // Flag to show "Reassign All" option (convert overrides to voice)
  showRemoveFromOrganization?: boolean; // Flag to show "Remove from Organization" option
  showManageAiVoices?: boolean; // Flag to show "Manage AI Voices" option (dubbing enabled)
}

/**
 * Context menu for speaker actions that appears when clicking the overflow button
 */
const SpeakerContextMenu: React.FC<SpeakerContextMenuProps> = ({
  isOpen,
  buttonRef,
  onClose,
  onEdit,
  onManageAiVoices,
  onAddToStory,
  onRemoveFromStory,
  onRemoveFromOrganization,
  onUnassign,
  onUndoSectionReassign,
  onUnassignAll,
  onReassignAll,
  onAssignToExisting,
  isAnonymous = false,
  showAddToStory = false,
  showRemoveFromStory = false,
  showUnassign = false,
  showUndoSectionReassign = false,
  showUnassignAll = false,
  showReassignAll = false,
  showRemoveFromOrganization = false,
  showManageAiVoices = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    if (!isOpen) return;

    // Track when the menu opened to ignore immediate mousedown events
    const openedAt = Date.now();
    const DEBOUNCE_MS = 150; // Ignore mousedown events within 150ms of opening

    const handleOutsideClick = (e: MouseEvent) => {
      const timeSinceOpen = Date.now() - openedAt;

      // Ignore clicks that happen immediately after opening
      if (timeSinceOpen < DEBOUNCE_MS) {
        return;
      }

      // Check if click is outside BOTH the menu AND the trigger button
      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(e.target as Node);
      const isOutsideButton = !buttonRef?.current?.contains(e.target as Node);

      if (isOutsideMenu && isOutsideButton) {
        onClose();
      }
    };

    // Handle escape key press
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Add listeners immediately - we'll use timestamp check to debounce
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, buttonRef]);

  // Memoize scrollable containers lookup to avoid repeated DOM traversals
  const scrollableContainers = useMemo(() => {
    if (!buttonRef?.current) return [];

    // Find scrollable parent of the button
    const findScrollableParent = () => {
      let parent = buttonRef.current?.parentElement;
      while (parent) {
        const overflow = window.getComputedStyle(parent).overflowY;
        if (overflow === 'auto' || overflow === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    return [
      document.querySelector('.transcript-scroll-container'),
      document.querySelector('[class*="SpeakersList"]'),
      findScrollableParent(),
    ].filter(Boolean) as Element[];
  }, [buttonRef]);

  // Calculate position relative to button
  useEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef?.current) return;

    const updatePosition = () => {
      if (!menuRef.current || !buttonRef?.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuElement = menuRef.current;
      const menuWidth = menuElement.offsetWidth;
      const menuHeight = menuElement.offsetHeight;

      // Calculate position - try left of button first, fall back to right if off-screen
      let left = buttonRect.left - menuWidth - 5;
      if (left < 0) {
        // Position to the right of the button instead
        left = buttonRect.right + 5;
      }

      // Ensure menu doesn't go off bottom of screen
      let top = buttonRect.top;
      if (top + menuHeight > window.innerHeight) {
        top = window.innerHeight - menuHeight - 10;
      }

      // Position menu
      menuElement.style.position = 'fixed';
      menuElement.style.top = `${top}px`;
      menuElement.style.left = `${left}px`;
    };

    // Initial position
    updatePosition();

    // Use requestAnimationFrame to debounce position updates
    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(updatePosition);
    };

    // Update position on scroll (using capture phase to catch all scroll events)
    const handleScroll = () => {
      scheduleUpdate();
    };

    // Update position on resize
    const handleResize = () => {
      scheduleUpdate();
    };

    // Listen to scroll events on window (capture phase catches all scroll events)
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // Also listen to scroll events on scrollable containers
    scrollableContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, true);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      scrollableContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll, true);
      });
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen, buttonRef, scrollableContainers]);

  if (!isOpen) return null;

  // Build menu items array, then interleave with dividers
  const items: React.ReactNode[] = [];

  if (isAnonymous && onAssignToExisting) {
    items.push(
      <MenuItem
        key="assign"
        onClick={() => {
          onAssignToExisting();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <MicrophoneIcon width="14" height="14" fillColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Assign Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  items.push(
    <MenuItem key="edit" onClick={onEdit}>
      <MenuContent>
        <MenuIconAndText>
          <MenuIconWrapper>
            {isAnonymous ? (
              <CirclePlusIcon width="14" height="14" color="currentColor" />
            ) : (
              <SpeakersIcon width="14" height="14" strokeColor="currentColor" />
            )}
          </MenuIconWrapper>
          <MenuLabel>
            {isAnonymous ? 'Create & Assign New Speaker' : 'Edit Speaker'}
          </MenuLabel>
        </MenuIconAndText>
      </MenuContent>
    </MenuItem>,
  );

  if (showManageAiVoices && onManageAiVoices && !isAnonymous) {
    items.push(
      <MenuItem key="manage-ai-voices" onClick={onManageAiVoices}>
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <MicrophoneIcon width="14" height="14" fillColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Manage AI Voices</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showAddToStory && onAddToStory && !isAnonymous) {
    items.push(
      <MenuItem
        key="add-story"
        onClick={() => {
          onAddToStory();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>📝</MenuIconWrapper>
            <MenuLabel>Add to Story</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showRemoveFromStory && onRemoveFromStory && !isAnonymous) {
    items.push(
      <MenuItem
        key="remove-story"
        onClick={() => {
          onRemoveFromStory();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <CloseIcon width="11" height="11" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Remove from Story</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (
    showRemoveFromStory &&
    onRemoveFromStory &&
    onRemoveFromOrganization &&
    !isAnonymous
  ) {
    items.push(
      <MenuItem
        key="remove-org-both"
        $danger
        onClick={() => {
          onRemoveFromStory();
          onRemoveFromOrganization();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <DeleteIcon width="13" height="13" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Remove from Organization</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showRemoveFromOrganization && onRemoveFromOrganization && !isAnonymous) {
    items.push(
      <MenuItem
        key="remove-org"
        $danger
        onClick={() => {
          onRemoveFromOrganization();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <DeleteIcon width="13" height="13" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Remove from Organization</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showUnassign && onUnassign && !isAnonymous) {
    items.push(
      <MenuItem
        key="unassign"
        onClick={() => {
          onUnassign();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <BrokenLinkIcon
                width="14"
                height="14"
                strokeColor="currentColor"
              />
            </MenuIconWrapper>
            <MenuLabel>Unassign Speaker</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showUndoSectionReassign && onUndoSectionReassign && !isAnonymous) {
    items.push(
      <MenuItem
        key="undo-reassign"
        onClick={() => {
          onUndoSectionReassign();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <UndoIcon width="14" height="14" strokeColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Undo Selection Reassign</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showUnassignAll && onUnassignAll && !isAnonymous) {
    items.push(
      <MenuItem
        key="unassign-all"
        onClick={() => {
          onUnassignAll();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <BrokenLinkIcon
                width="14"
                height="14"
                strokeColor="currentColor"
              />
            </MenuIconWrapper>
            <MenuLabel>Unassign All</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  if (showReassignAll && onReassignAll && !isAnonymous) {
    items.push(
      <MenuItem
        key="reassign-all"
        onClick={() => {
          onReassignAll();
          onClose();
        }}
      >
        <MenuContent>
          <MenuIconAndText>
            <MenuIconWrapper>
              <ReassignIcon width="14" height="14" fillColor="currentColor" />
            </MenuIconWrapper>
            <MenuLabel>Reassign All</MenuLabel>
          </MenuIconAndText>
        </MenuContent>
      </MenuItem>,
    );
  }

  const menuContent = (
    <MenuContainer
      ref={menuRef}
      data-speaker-menu={true}
      data-testid="speaker-context-menu"
    >
      <MenuItems>{items}</MenuItems>
    </MenuContainer>
  );

  // Render in a portal to ensure proper positioning outside any parent containers
  return ReactDOM.createPortal(menuContent, document.body);
};

const MenuContainer = styled.div`
  position: fixed; /* Will be positioned programmatically in useEffect */
  background: var(--background);
  box-shadow: var(--elevation-toast);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  // Resting z is the historical 1000005; only the clip-preview review lifts it
  // (via the --top-layer-context-menu-z var) so it clears that workspace
  // (1000030) while staying below the speaker Add/Edit modal it spawns. At rest
  // every other surface is unchanged (PLAT-10286).
  z-index: var(${TOP_LAYER_VARS.contextMenu}, 1000005);
`;

const MenuItems = styled.div`
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: var(--spacing-xs);
  overflow: hidden;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  padding: 1px 6px;
  width: 100%;
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
  border-radius: var(--radius-button);
  color: ${props =>
    props.$danger ? 'var(--destructive)' : 'var(--foreground)'};
  ${props =>
    props.$danger &&
    `
    p { color: var(--destructive); }
  `}

  &:hover {
    background: ${props =>
      props.$danger ? 'var(--destructive)' : 'var(--surface-elevated)'};
    ${props =>
      props.$danger &&
      `
      p, svg { color: var(--destructive-foreground); }
    `}
  }
`;

const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 9px 10px;
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
`;

const MenuIconAndText = styled.div`
  display: flex;
  align-items: center;
  text-align: start;
  gap: 8px;
  width: 100%;
`;

const MenuIconWrapper = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuLabel = styled.p`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  color: var(--foreground);
  margin: 0;
  white-space: nowrap;
`;

export default SpeakerContextMenu;
